import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Search, X, Stethoscope, Eye, CheckCircle2, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { getSpecialties, createSpecialty, updateSpecialty, deleteSpecialty, toggleSpecialtyStatus, checkUsedByDoctors, checkSpecilityName, clearSpecialtyNameCheck, getSpCategoryList, downloadSpecialtySampleExcel, bulkUploadSpecialties, checkSpecialtyBulkFormat } from '../../redux/masterSlice';
import ErrorPage from '../../components/ErrorPage';
import MasterLabel from '../../components/common/MasterLabel';
import { useMasterLabel } from '../../hooks/useMasterLabel';

// Tiny wrapper so the translation hook can run per row inside a <select> map.
// Rules-of-hooks prevent calling useMasterLabel directly inside the map callback.
const CategoryOption = ({ cat }) => {
    const label = useMasterLabel('masterSpecialtyCategory', cat.spcategoryId, 'spcategoryName', cat.spcategoryName);
    return <option value={cat.spcategoryId}>{label}</option>;
};

const Speciality = () => {
    const { t } = useTranslation('specialitymaster');
    const dispatch = useDispatch();
    const { specialties, specialtyLoading, specialtyCreateLoading, specialtyUpdateLoading, specialtyDeleteLoading, specialtyToggleLoading, specialtyPagination, specialtyNameCheckLoading, specialtyNameCheckResult, spCategoryList, spCategoryListLoading, specialtyBulkUploadLoading, specialtyDownloadLoading, error } = useSelector((state) => state.master);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingSpecialty, setViewingSpecialty] = useState(null);
    const [editingSpecialty, setEditingSpecialty] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        categoryId: ''
    });
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'

    // Bulk upload state
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [bulkUploadFile, setBulkUploadFile] = useState(null);
    const [bulkUploadResult, setBulkUploadResult] = useState(null);
    const [bulkFileError, setBulkFileError] = useState('');
    const [bulkFormatChecking, setBulkFormatChecking] = useState(false);
    const [bulkFormatValid, setBulkFormatValid] = useState(false);
    const fileInputRef = useRef(null);

    // Debounce search query
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Debounce specialty name check
    const [debouncedNameCheck, setDebouncedNameCheck] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedNameCheck(formData.name.trim());
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.name]);

    // Check specialty name when it changes
    useEffect(() => {
        if (debouncedNameCheck && debouncedNameCheck.length > 0) {
            if (!editingSpecialty || (editingSpecialty && debouncedNameCheck !== editingSpecialty.name)) {
                dispatch(checkSpecilityName(debouncedNameCheck));
            } else {
                dispatch(clearSpecialtyNameCheck());
            }
        } else {
            dispatch(clearSpecialtyNameCheck());
        }
    }, [debouncedNameCheck, editingSpecialty, dispatch]);

    // Fetch category list for dropdown
    useEffect(() => {
        dispatch(getSpCategoryList());
    }, [dispatch]);

    // Fetch specialties when search/page/tab changes
    useEffect(() => {
        dispatch(getSpecialties({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, activeTab]);

    const filteredSpecialties = specialties || [];
    const paginatedSpecialties = filteredSpecialties;

    // Pagination handler
    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Maximum length constants
    const MAX_SEARCH_LENGTH = 50;
    const MAX_NAME_LENGTH = 50;
    const MAX_DESCRIPTION_LENGTH = 200;

    // Validate search input
    const validateSearchInput = (value) => {
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            return t('specialitymaster.search_exceed_limit', `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters`);
        }
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('specialitymaster.search_only_spaces', 'Search term cannot contain only spaces');
        }
        return '';
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > MAX_SEARCH_LENGTH
            ? sanitized.substring(0, MAX_SEARCH_LENGTH)
            : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchError(error);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'name') {
            processedValue = value.replace(/[<>]/g, '');
            if (processedValue.length > MAX_NAME_LENGTH) {
                processedValue = processedValue.substring(0, MAX_NAME_LENGTH);
            }
        } else if (name === 'description') {
            processedValue = value.replace(/[<>]/g, '');
            if (processedValue.length > MAX_DESCRIPTION_LENGTH) {
                processedValue = processedValue.substring(0, MAX_DESCRIPTION_LENGTH);
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('specialitymaster.name_required', 'Specialty name is required');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('specialitymaster.name_min_length', 'Specialty name must be at least 2 characters');
        } else if (formData.name.trim().length > MAX_NAME_LENGTH) {
            newErrors.name = t('specialitymaster.name_max_length', `Specialty name cannot exceed ${MAX_NAME_LENGTH} characters`);
        } else if (!/^[A-Za-zÀ-ÿ\s\-']+$/.test(formData.name.trim())) {
            newErrors.name = t('specialitymaster.name_invalid_chars', 'Specialty name can only contain letters, spaces, hyphens, and apostrophes');
        }

        if (!formData.categoryId) {
            newErrors.categoryId = t('specialitymaster.category_required', 'Category is required');
        }

        if (formData.name.trim() && specialtyNameCheckResult?.isDuplicate) {
            const errorMessage = specialtyNameCheckResult?.message?.en || t('specialitymaster.name_already_exists', 'This specialty name already exists');
            newErrors.name = errorMessage;
        }

        if (!formData.description.trim()) {
            newErrors.description = t('specialitymaster.description_required', 'Description is required');
        } else if (formData.description.trim().length < 5) {
            newErrors.description = t('specialitymaster.description_min_length', 'Description must be at least 5 characters');
        } else if (formData.description.length > MAX_DESCRIPTION_LENGTH) {
            newErrors.description = t('specialitymaster.description_max_length', `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
        } else if (formData.description.trim().length === 0 && formData.description.length > 0) {
            newErrors.description = t('specialitymaster.description_only_spaces', 'Description cannot contain only spaces');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle add new specialty
    const handleAdd = () => {
        setEditingSpecialty(null);
        setFormData({ name: '', description: '', categoryId: '' });
        setErrors({});
        dispatch(clearSpecialtyNameCheck());
        setShowModal(true);
    };

    // Handle edit specialty
    const handleEdit = (specialty) => {
        setEditingSpecialty(specialty);
        setFormData({
            name: specialty.name || '',
            description: specialty.description || '',
            categoryId: specialty.categoryId || ''
        });
        setErrors({});
        dispatch(clearSpecialtyNameCheck());
        setShowModal(true);
    };

    // Helper to refresh list with current filters
    const refreshList = (page = currentPage) => {
        dispatch(getSpecialties({
            page,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    };

    // Handle delete specialty
    const handleDelete = async (specialty) => {
        try {
            // Check if specialty is used by doctors first
            const usageResult = await dispatch(checkUsedByDoctors({ master: 'speciality', id: specialty.specialtyId })).unwrap();
            const isUsed = usageResult?.isUsed;
            const count = usageResult?.count || 0;

            if (isUsed) {
                toast.warn(t('specialitymaster.delete_used_by_doctors', { name: specialty.name, count, defaultValue: `"${specialty.name}" is currently used by ${count} doctor(s). It cannot be deleted.` }));
                return;
            }

            const result = await Swal.fire({
                title: t('specialitymaster.delete_specialty_title'),
                text: t('specialitymaster.delete_specialty_text', { name: specialty.name }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#4ABDBD',
                confirmButtonText: t('specialitymaster.yes_delete'),
                cancelButtonText: t('specialitymaster.cancel')
            });

            if (result.isConfirmed) {
                const id = specialty.specialtyId;
                await dispatch(deleteSpecialty(id)).unwrap();
                toast.success(t('specialitymaster.deleted_success', 'Specialty has been deleted successfully.'));
                refreshList();
            }
        } catch (error) {
            toast.warn(error?.error || t('specialitymaster.delete_failed', 'Failed to delete specialty.'));
        }
    };

    // Handle toggle status
    const handleStatusToggle = async (specialty) => {
        const currentStatus = specialty.status;
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

        try {
            // Only check doctor usage when deactivating (active -> inactive)
            let isUsed = false;
            let count = 0;
            if (newStatus === 'inactive') {
                const usageResult = await dispatch(checkUsedByDoctors({ master: 'speciality', id: specialty.specialtyId })).unwrap();
                isUsed = usageResult?.isUsed;
                count = usageResult?.count || 0;
            }

            if (isUsed) {
                toast.warn(t('specialitymaster.deactivate_used_by_doctors', { name: specialty.name, count, defaultValue: `"${specialty.name}" is currently used by ${count} doctor(s). It cannot be deactivated.` }));
                return;
            }

            const result = await Swal.fire({
                title: t('specialitymaster.status_change_title'),
                text: t('specialitymaster.status_change_text', { action: newStatus === 'active' ? t('specialitymaster.activate') : t('specialitymaster.deactivate'), name: specialty.name }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('specialitymaster.confirm'),
                cancelButtonText: t('specialitymaster.cancel'),
            });

            if (!result.isConfirmed) return;

            await dispatch(toggleSpecialtyStatus({
                id: specialty.specialtyId,
                status: newStatus
            })).unwrap();

            toast.success(newStatus === 'active'
                    ? t('specialitymaster.activated_success')
                    : t('specialitymaster.deactivated_success'));
            refreshList();
        } catch (error) {
            toast.warn(error?.error || t('specialitymaster.status_update_failed', 'Failed to update specialty status.'));
        }
    };

    // Handle save (create or update)
    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            if (editingSpecialty) {
                const id = editingSpecialty.specialtyId;
                await dispatch(updateSpecialty({
                    id: id,
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                    categoryId: formData.categoryId
                })).unwrap();

                toast.success(t('specialitymaster.updated_success', 'Specialty has been updated successfully.'));
                refreshList();
            } else {
                await dispatch(createSpecialty({
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                    categoryId: formData.categoryId
                })).unwrap();

                toast.success(t('specialitymaster.created_success', 'Specialty has been created successfully.'));
                setCurrentPage(1);
                refreshList(1);
            }

            setShowModal(false);
            setFormData({ name: '', description: '', categoryId: '' });
            setEditingSpecialty(null);
            setErrors({});
        } catch (error) {
            toast.warn(error?.error || (editingSpecialty ? t('specialitymaster.update_failed', 'Failed to update specialty.') : t('specialitymaster.create_failed', 'Failed to create specialty.')));
        }
    };

    // Handle modal close
    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ name: '', description: '', categoryId: '' });
        setEditingSpecialty(null);
        setErrors({});
        dispatch(clearSpecialtyNameCheck());
    };

    // Handle view specialty
    const handleView = (specialty) => {
        setViewingSpecialty(specialty);
        setShowViewModal(true);
    };

    // Handle close view modal
    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setViewingSpecialty(null);
    };

    // Bulk Upload Handlers
    const handleOpenBulkUpload = () => {
        setBulkUploadFile(null);
        setBulkUploadResult(null);
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkFormatChecking(false);
        setShowBulkUploadModal(true);
    };

    const handleCloseBulkUpload = () => {
        setShowBulkUploadModal(false);
        setBulkUploadFile(null);
        setBulkUploadResult(null);
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkFormatChecking(false);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkUploadResult(null);
        if (!file) {
            setBulkUploadFile(null);
            return;
        }

        const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
        const allowedExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!validTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            setBulkFileError(t('specialitymaster.only_excel_files', 'Only .xlsx and .xls files are allowed.'));
            setBulkUploadFile(null);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setBulkFileError(t('specialitymaster.max_file_size', 'Maximum file size is 5MB.'));
            setBulkUploadFile(null);
            return;
        }
        setBulkUploadFile(file);

        // Verify the file structure with the backend before allowing upload.
        setBulkFormatChecking(true);
        try {
            await dispatch(checkSpecialtyBulkFormat(file)).unwrap();
            setBulkFormatValid(true);
        } catch (err) {
            setBulkFormatValid(false);
            setBulkFileError(err?.error || t('specialitymaster.invalid_file_format', 'Invalid Excel format'));
        } finally {
            setBulkFormatChecking(false);
        }
    };

    const handleDownloadSample = () => {
        dispatch(downloadSpecialtySampleExcel());
    };

    const handleBulkUpload = async () => {
        if (!bulkUploadFile) return;
        if (!bulkFormatValid) {
            setBulkFileError(t('specialitymaster.fix_format_first', 'Please fix the file format errors before uploading.'));
            return;
        }

        try {
            const result = await dispatch(bulkUploadSpecialties(bulkUploadFile)).unwrap();
            setBulkUploadResult(result);
            refreshList();
        } catch (error) {
            toast.warn(error?.error || t('specialitymaster.bulk_upload_failed', 'Failed to upload file.'));
        }
    };

    // Handle error state — only blank the page when the initial fetch fails
    // (no data yet). Per-action failures (delete/update/create/toggle) shouldn't
    // hijack the whole page; they're surfaced via toast/Swal in their handlers.
    const hasNoSpecialties = !specialties || specialties.length === 0;
    if (error && hasNoSpecialties && !specialtyLoading) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={error} pageName={t('specialitymaster.page_name', 'Specialty Management')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={error} pageName={t('specialitymaster.page_name', 'Specialty Management')} />;
        }
        return <ErrorPage error={error} pageName={t('specialitymaster.page_name', 'Specialty Management')} />;
    }

    return (
        <div className="w-full" data-testid="speciality-master-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-4" data-testid="speciality-master-header">
                    <h1 className="text-2xl sm:text-3xl pb-2 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2" data-testid="speciality-master-title">
                        {t('specialitymaster.specialty_management')}
                    </h1>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleAdd}
                            className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                            data-testid="speciality-master-add-button"
                        >
                            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" data-testid="speciality-master-add-icon" />
                            {t('specialitymaster.add_specialty')}
                        </button>
                                                <button
                            onClick={handleOpenBulkUpload}
                            className="group flex items-center gap-2 px-4 py-2 border-2 border-[#3AA8A8] text-[#3AA8A8] rounded-xl hover:bg-[#3AA8A8] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
                            data-testid="speciality-master-bulk-upload-button"
                        >
                            <Upload size={20} className="group-hover:scale-110 transition-transform duration-300" data-testid="speciality-master-bulk-upload-icon" />
                            {t('specialitymaster.bulk_upload', 'Bulk Upload')}
                        </button>
                    </div>
                </div>

                {/* Tab Bar + Search Row */}
                <div className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="speciality-master-tab-search-section">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2" data-testid="speciality-master-tabs">
                        <button
                            onClick={() => { setActiveTab('active'); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'active'
                                ? 'text-white'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                            data-testid="speciality-master-tab-active"
                        >
                            {activeTab === 'active' && (
                                <motion.div
                                    layoutId="activeSpecialtyStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20" data-testid="speciality-master-tab-active-text">{t('specialitymaster.active') || 'Active'}</span>
                        </button>

                        <button
                            onClick={() => { setActiveTab('inactive'); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'inactive'
                                ? 'text-white'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                            data-testid="speciality-master-tab-inactive"
                        >
                            {activeTab === 'inactive' && (
                                <motion.div
                                    layoutId="activeSpecialtyStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20" data-testid="speciality-master-tab-inactive-text">{t('specialitymaster.inactive') || 'Inactive'}</span>
                        </button>
                    </div>
                    <div className="relative group w-full sm:w-1/3" data-testid="speciality-master-search-container">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={22} data-testid="speciality-master-search-icon" />
                        <input
                            type="text"
                            placeholder={t('specialitymaster.search_placeholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={50}
                            className={`w-full pl-12 pr-4 py-2 bg-gray-50 border-2 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 ${searchError
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-200'
                                }`}
                            data-testid="speciality-master-search-input"
                        />
                        {searchError && (
                            <p className="text-red-600 text-xs mt-1 ml-1" data-testid="speciality-master-search-error">{searchError}</p>
                        )}
                    </div>
                </div>

                {/* Specialties Grid */}
                {(!specialtyLoading && filteredSpecialties.length === 0) ? (
                    <div className="text-center py-16 sm:py-20" data-testid="speciality-master-empty-container">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Stethoscope size={48} className="text-gray-400" data-testid="speciality-master-empty-icon" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2" data-testid="speciality-master-empty-title">
                            {searchQuery ? t('specialitymaster.no_specialties_found') : t('specialitymaster.no_specialties_yet')}
                        </h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto" data-testid="speciality-master-empty-text">
                            {searchQuery
                                ? t('specialitymaster.try_adjusting_search')
                                : t('specialitymaster.create_first_specialty')}
                        </p>
                        {!searchQuery && activeTab === 'active' && (
                            <button
                                onClick={handleAdd}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                                data-testid="speciality-master-empty-create-button"
                            >
                                <Plus size={20} data-testid="speciality-master-empty-create-icon" />
                                {t('specialitymaster.create_first_specialty_button')}
                            </button>
                        )}
                    </div>
                ) : specialtyLoading ? (
                    <div className="flex items-center justify-center py-16 sm:py-20" data-testid="speciality-master-loading-container">
                        <div className="flex flex-col items-center gap-4">
                            <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin" data-testid="speciality-master-loading-spinner"></div>
                            <p className="text-gray-500 text-sm" data-testid="speciality-master-loading-text">{t('specialitymaster.loading_specialties')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-4" data-testid="speciality-master-grid">
                            {paginatedSpecialties.map((specialty) => {
                                const isInactive = specialty.status === 'inactive';
                                const spId = specialty.specialtyId || specialty._id || specialty.id;
                                return (
                                    <div
                                        key={spId}
                                        className={`group relative bg-white border rounded-lg p-4 overflow-hidden transition-all duration-300
                                        ${isInactive
                                                ? 'border-gray-200 opacity-75'
                                                : 'border-gray-200 hover:border-[#4ABDBD] hover:shadow-lg transform hover:-translate-y-0.5 cursor-pointer'
                                            }`}
                                        data-testid={`speciality-master-card-${spId}`}
                                    >
                                        {/* Gradient Accent - only for active */}
                                        {!isInactive && (
                                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                                        )}

                                        {/* Toggle Switch - top right */}
                                        <div className="absolute top-3 right-3 z-20">
                                            <div
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${isInactive
                                                    ? 'bg-gray-300'
                                                    : 'bg-[#4ABDBD]'
                                                    } ${specialtyToggleLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                onClick={() => !specialtyToggleLoading && handleStatusToggle(specialty)}
                                                data-testid={`speciality-master-toggle-${spId}`}
                                            >
                                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${isInactive
                                                    ? 'translate-x-1'
                                                    : 'translate-x-[18px]'
                                                    }`}></span>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="mb-3 pr-12">
                                            <h3 className={`text-base font-bold line-clamp-1 mb-2 transition-colors
                                            ${isInactive ? 'text-gray-400' : 'text-gray-800 group-hover:text-[#4ABDBD]'}`}
                                                data-testid={`speciality-master-card-name-${spId}`}>
                                                <MasterLabel type="masterSpecialty" id={specialty.specialtyId} field="name" fallback={specialty.name} />
                                            </h3>
                                            {specialty.categoryName && (
                                                <p className={`text-xs mb-1 ${isInactive ? 'text-gray-400' : 'text-[#4ABDBD]'}`}
                                                    data-testid={`speciality-master-card-category-${spId}`}>
                                                    <MasterLabel type="masterSpecialtyCategory" id={specialty.categoryId} field="spcategoryName" fallback={specialty.categoryName} />
                                                </p>
                                            )}
                                            <p className={`text-xs line-clamp-2 leading-relaxed min-h-[32px] ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}
                                                data-testid={`speciality-master-card-description-${spId}`}>
                                                <MasterLabel type="masterSpecialty" id={specialty.specialtyId} field="description" fallback={specialty.description || t('specialitymaster.no_description_provided')} />
                                            </p>
                                        </div>

                                        {/* Actions - available for both active and inactive */}
                                        <div className="flex items-center justify-center gap-2 pt-3 border-t border-gray-100">
                                            <button
                                                onClick={() => handleView(specialty)}
                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                title={t('specialitymaster.view_details')}
                                                data-testid={`speciality-master-view-button-${spId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Eye className="h-4 w-4 relative z-10" data-testid={`speciality-master-view-icon-${spId}`} />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(specialty)}
                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                title={t('specialitymaster.edit')}
                                                data-testid={`speciality-master-edit-button-${spId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Edit2 className="h-4 w-4 relative z-10" data-testid={`speciality-master-edit-icon-${spId}`} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(specialty)}
                                                disabled={specialtyDeleteLoading}
                                                className="group/btn text-red-500 hover:text-white p-2 rounded-lg bg-gradient-to-r from-red-50 to-red-50 hover:from-red-500 hover:to-red-600 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={t('specialitymaster.delete')}
                                                data-testid={`speciality-master-delete-button-${spId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Trash2 className="h-4 w-4 relative z-10" data-testid={`speciality-master-delete-icon-${spId}`} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Pagination */}
                        {specialtyPagination.total > 0 && (
                            <Pagination
                                totalItems={specialtyPagination.total}
                                currentPage={specialtyPagination.page || currentPage}
                                itemsPerPage={specialtyPagination.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                                custombutton={paginatedSpecialties.length === 0 ? 'disable' : ''}
                            />
                        )}
                    </>
                )}

                {/* Create/Edit Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="speciality-master-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="speciality-master-modal-content">
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="speciality-master-modal-title">
                                    {editingSpecialty ? t('specialitymaster.edit_specialty') : t('specialitymaster.add_new_specialty')}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="speciality-master-modal-close-button"
                                >
                                    <X size={24} data-testid="speciality-master-modal-close-icon" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-6" data-testid="speciality-master-form">
                                {/* Category Dropdown */}
                                <div data-testid="speciality-master-category-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-master-category-label">
                                        {t('specialitymaster.category_label', 'Category')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="categoryId"
                                        value={formData.categoryId}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.categoryId
                                            ? 'border-red-300 bg-red-50'
                                            : 'border-gray-300'
                                            }`}
                                        data-testid="speciality-master-category-dropdown"
                                    >
                                        <option value="" data-testid="speciality-master-category-option-empty">{t('specialitymaster.select_category', 'Select a category')}</option>
                                        {spCategoryList.map((cat) => (
                                            <CategoryOption key={cat.spcategoryId} cat={cat} />
                                        ))}
                                    </select>
                                    {errors.categoryId && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="speciality-master-category-error">{errors.categoryId}</p>
                                    )}
                                </div>

                                {/* Specialty Name */}
                                <div data-testid="speciality-master-name-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-master-name-label">
                                        {t('specialitymaster.specialty_name_label')}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            maxLength={MAX_NAME_LENGTH}
                                            className={`w-full px-4 py-2 pr-10 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.name || (specialtyNameCheckResult?.isDuplicate && formData.name.trim())
                                                ? 'border-red-300 bg-red-50'
                                                : specialtyNameCheckResult && !specialtyNameCheckResult.isDuplicate && formData.name.trim() && !specialtyNameCheckLoading
                                                    ? 'border-green-300'
                                                    : 'border-gray-300'
                                                }`}
                                            placeholder={t('specialitymaster.specialty_name_placeholder')}
                                            data-testid="speciality-master-name-input"
                                        />
                                        {formData.name.trim() && !specialtyNameCheckLoading && specialtyNameCheckResult && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                {specialtyNameCheckResult.isDuplicate ? (
                                                    <X className="h-5 w-5 text-red-500" data-testid="speciality-master-name-duplicate-icon" />
                                                ) : (
                                                    <CheckCircle2 className="h-5 w-5 text-green-500" data-testid="speciality-master-name-available-icon" />
                                                )}
                                            </div>
                                        )}
                                        {specialtyNameCheckLoading && formData.name.trim() && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <div className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-5 h-5 animate-spin" data-testid="speciality-master-name-check-loading"></div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="speciality-master-name-error">{errors.name}</p>
                                    )}
                                    {!errors.name && specialtyNameCheckResult?.isDuplicate && formData.name.trim() && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="speciality-master-name-duplicate-error">
                                            {specialtyNameCheckResult?.message?.en || t('specialitymaster.name_already_exists', 'This specialty name already exists')}
                                        </p>
                                    )}
                                </div>

                                {/* Description */}
                                <div data-testid="speciality-master-description-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-master-description-label">
                                        {t('specialitymaster.short_description_label')} <span className="text-gray-400">{t('specialitymaster.max_characters')}</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={4}
                                        maxLength={MAX_DESCRIPTION_LENGTH}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent resize-none transition-all ${errors.description
                                            ? 'border-red-300 bg-red-50'
                                            : 'border-gray-300'
                                            }`}
                                        placeholder={t('specialitymaster.description_placeholder')}
                                        data-testid="speciality-master-description-input"
                                    />
                                    <div className="flex items-center justify-between mt-1">
                                        {errors.description && (
                                            <p className="text-sm text-red-500" data-testid="speciality-master-description-error">{errors.description}</p>
                                        )}
                                        <p className={`text-xs ml-auto ${formData.description.length > 180 ? 'text-amber-500' : 'text-gray-400'
                                            }`}
                                            data-testid="speciality-master-description-counter">
                                            {t('specialitymaster.characters_count', { count: formData.description.length })}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseModal}
                                    disabled={specialtyCreateLoading || specialtyUpdateLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="speciality-master-modal-cancel-button"
                                >
                                    {t('specialitymaster.cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={specialtyCreateLoading || specialtyUpdateLoading}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="speciality-master-modal-save-button"
                                >
                                    {(specialtyCreateLoading || specialtyUpdateLoading) && (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="speciality-master-modal-save-loading"></span>
                                    )}
                                    {specialtyCreateLoading || specialtyUpdateLoading
                                        ? (editingSpecialty ? t('specialitymaster.updating') : t('specialitymaster.creating'))
                                        : (editingSpecialty ? t('specialitymaster.update') : t('specialitymaster.create'))}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View Specialty Modal */}
                {showViewModal && viewingSpecialty && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="speciality-master-view-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="speciality-master-view-modal-content">
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="speciality-master-view-modal-title">
                                    {t('specialitymaster.specialty_details')}
                                </h2>
                                <button
                                    onClick={handleCloseViewModal}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="speciality-master-view-modal-close-button"
                                >
                                    <X size={24} data-testid="speciality-master-view-modal-close-icon" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-6">
                                {/* Category */}
                                {viewingSpecialty.categoryName && (
                                    <div data-testid="speciality-master-view-category-field-container">
                                        <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-master-view-category-label">
                                            {t('specialitymaster.category_label', 'Category')}
                                        </label>
                                        <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-semibold" data-testid="speciality-master-view-category-value">
                                            <MasterLabel type="masterSpecialtyCategory" id={viewingSpecialty.categoryId} field="spcategoryName" fallback={viewingSpecialty.categoryName} />
                                        </div>
                                    </div>
                                )}

                                {/* Specialty Name */}
                                <div data-testid="speciality-master-view-name-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-master-view-name-label">
                                        {t('specialitymaster.specialty_name')}
                                    </label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-semibold" data-testid="speciality-master-view-name-value">
                                        <MasterLabel type="masterSpecialty" id={viewingSpecialty.specialtyId} field="name" fallback={viewingSpecialty.name} />
                                    </div>
                                </div>

                                {/* Description */}
                                <div data-testid="speciality-master-view-description-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-master-view-description-label">
                                        {t('specialitymaster.description')}
                                    </label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-700 min-h-[120px] whitespace-pre-wrap" data-testid="speciality-master-view-description-value">
                                        <MasterLabel type="masterSpecialty" id={viewingSpecialty.specialtyId} field="description" fallback={viewingSpecialty.description || t('specialitymaster.no_description_provided')} />
                                    </div>
                                </div>

                                {/* Status */}
                                <div data-testid="speciality-master-view-status-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-master-view-status-label">
                                        {t('specialitymaster.status_label', 'Status')}
                                    </label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${viewingSpecialty.status === 'active'
                                            ? 'bg-green-100 text-green-700 border border-green-300'
                                            : 'bg-gray-200 text-gray-500 border border-gray-300'
                                            }`}
                                            data-testid="speciality-master-view-status-value">
                                            {viewingSpecialty.status === 'active' ? t('specialitymaster.active', 'Active') : t('specialitymaster.inactive', 'Inactive')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
                                <button
                                    onClick={handleCloseViewModal}
                                    className="px-6 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md"
                                    data-testid="speciality-master-view-modal-close-footer-button"
                                >
                                    {t('specialitymaster.close')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bulk Upload Modal */}
                {showBulkUploadModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="speciality-master-bulk-upload-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="speciality-master-bulk-upload-modal-content">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="speciality-master-bulk-upload-modal-title">
                                    {t('specialitymaster.bulk_upload_title', 'Bulk Upload Specialties')}
                                </h2>
                                <button
                                    onClick={handleCloseBulkUpload}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="speciality-master-bulk-upload-modal-close-button"
                                >
                                    <X size={24} data-testid="speciality-master-bulk-upload-modal-close-icon" />
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Download Sample */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="speciality-master-download-sample-section">
                                    <div className="flex items-start gap-3">
                                        <FileSpreadsheet size={24} className="text-blue-500 mt-0.5" data-testid="speciality-master-download-sample-icon" />
                                        <div className="flex-1">
                                            <h3 className="text-sm font-semibold text-blue-800 mb-1" data-testid="speciality-master-download-sample-title">
                                                {t('specialitymaster.download_sample_title', 'Download Sample Template')}
                                            </h3>
                                            <p className="text-xs text-blue-600 mb-2" data-testid="speciality-master-download-sample-description">
                                                {t('specialitymaster.download_sample_desc', 'Download the sample Excel template with correct format (Name, Description, CategoryId) before uploading.')}
                                            </p>
                                            <button
                                                onClick={handleDownloadSample}
                                                disabled={specialtyDownloadLoading}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-testid="speciality-master-download-template-button"
                                            >
                                                {specialtyDownloadLoading ? (
                                                    <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="speciality-master-download-template-loading"></span>
                                                ) : (
                                                    <Download size={16} data-testid="speciality-master-download-template-icon" />
                                                )}
                                                {t('specialitymaster.download_template', 'Download Template')}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* File Upload */}
                                <div data-testid="speciality-master-file-upload-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-master-file-upload-label">
                                        {t('specialitymaster.select_file', 'Select Excel File')}
                                    </label>
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#4ABDBD] transition-colors"
                                        data-testid="speciality-master-file-dropzone"
                                    >
                                        <Upload size={32} className="mx-auto text-gray-400 mb-2" data-testid="speciality-master-file-dropzone-icon" />
                                        <p className="text-sm text-gray-600" data-testid="speciality-master-file-dropzone-text">
                                            {bulkUploadFile
                                                ? bulkUploadFile.name
                                                : t('specialitymaster.click_to_select', 'Click to select an Excel file (.xlsx, .xls)')}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1" data-testid="speciality-master-file-dropzone-hint">
                                            {t('specialitymaster.max_size', 'Maximum file size: 5MB')}
                                        </p>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        data-testid="speciality-master-file-input"
                                    />
                                    {bulkFormatChecking && (
                                        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1" data-testid="speciality-master-format-checking">
                                            <span className="border-2 border-gray-400 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                            {t('specialitymaster.checking_format', 'Checking file format...')}
                                        </p>
                                    )}
                                    {!bulkFormatChecking && bulkUploadFile && bulkFormatValid && !bulkFileError && (
                                        <p className="mt-2 text-xs text-green-600 flex items-center gap-1" data-testid="speciality-master-format-valid">
                                            <CheckCircle size={12} />
                                            {t('specialitymaster.format_valid', 'File format looks good')}
                                        </p>
                                    )}
                                    {bulkFileError && (
                                        <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-testid="speciality-master-file-error">
                                            <AlertCircle size={14} />
                                            {bulkFileError}
                                        </p>
                                    )}
                                </div>

                                {/* Upload Result */}
                                {bulkUploadResult && (
                                    <div className="space-y-3" data-testid="speciality-master-upload-result-container">
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="speciality-master-upload-summary-section">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CheckCircle size={18} className="text-green-500" data-testid="speciality-master-upload-summary-icon" />
                                                <span className="text-sm font-semibold text-green-800" data-testid="speciality-master-upload-summary-title">
                                                    {t('specialitymaster.upload_complete', 'Upload Complete')}
                                                </span>
                                            </div>
                                            <div className="text-xs text-green-700 space-y-1">
                                                <p data-testid="speciality-master-created-count-text">{t('specialitymaster.created_count', { count: bulkUploadResult.summary?.totalCreated || 0, defaultValue: `Created: ${bulkUploadResult.summary?.totalCreated || 0}` })}</p>
                                                <p data-testid="speciality-master-duplicates-count-text">{t('specialitymaster.duplicates_count', { count: bulkUploadResult.summary?.totalDuplicates || 0, defaultValue: `Duplicates skipped: ${bulkUploadResult.summary?.totalDuplicates || 0}` })}</p>
                                                <p data-testid="speciality-master-errors-count-text">{t('specialitymaster.errors_count', { count: bulkUploadResult.summary?.totalErrors || 0, defaultValue: `Errors: ${bulkUploadResult.summary?.totalErrors || 0}` })}</p>
                                            </div>
                                        </div>

                                        {bulkUploadResult.duplicates?.length > 0 && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="speciality-master-duplicates-section">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <AlertCircle size={18} className="text-amber-500" data-testid="speciality-master-duplicates-icon" />
                                                    <span className="text-sm font-semibold text-amber-800" data-testid="speciality-master-duplicates-title">
                                                        {t('specialitymaster.duplicates_found', 'Duplicates Found')}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-amber-700 space-y-1 max-h-24 overflow-y-auto">
                                                    {bulkUploadResult.duplicates.map((dup, idx) => (
                                                        <p key={idx} data-testid={`speciality-master-duplicate-row-${idx}`}>{t('specialitymaster.row', 'Row')} {dup.row}: {dup.name} ({t('specialitymaster.existing', 'existing')}: {dup.existingId})</p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {bulkUploadResult.errors?.length > 0 && (
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="speciality-master-errors-section">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <AlertCircle size={18} className="text-red-500" data-testid="speciality-master-errors-icon" />
                                                    <span className="text-sm font-semibold text-red-800" data-testid="speciality-master-errors-title">
                                                        {t('specialitymaster.errors_found', 'Errors Found')}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-red-700 space-y-1 max-h-24 overflow-y-auto">
                                                    {bulkUploadResult.errors.map((err, idx) => (
                                                        <p key={idx} data-testid={`speciality-master-error-row-${idx}`}>{t('specialitymaster.row', 'Row')} {err.row}: {err.name} - {err.error}</p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseBulkUpload}
                                    disabled={specialtyBulkUploadLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="speciality-master-bulk-upload-modal-close-footer-button"
                                >
                                    {t('specialitymaster.close')}
                                </button>
                                {!bulkUploadResult && (
                                    <button
                                        onClick={handleBulkUpload}
                                        disabled={!bulkUploadFile || specialtyBulkUploadLoading || bulkFormatChecking || !bulkFormatValid}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-testid="speciality-master-bulk-upload-submit-button"
                                    >
                                        {specialtyBulkUploadLoading && (
                                            <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="speciality-master-bulk-upload-submit-loading"></span>
                                        )}
                                        {specialtyBulkUploadLoading
                                            ? t('specialitymaster.uploading', 'Uploading...')
                                            : t('specialitymaster.upload_btn', 'Upload')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Speciality;
